import moment from 'moment';
import { Href } from 'redux-little-router';

import { BannerPrice } from './banners';
import { BannerCreativeData } from './bannersEditState';
import { CampaignStrategyBudgetInfo } from './budget';
import { BrandSurveyStatus } from './campaigns';
import { NumberRangeInputValues, OperatorModerationInfo } from './common';
import { FeedCategoryNode } from './feedCategories';
import { GoalStats } from './goals';
import {
    CurrencyCode,
    GdAdGroupModerationStatus,
    GdAdGroupPrimaryStatus,
    GdAdGroupPrimaryStatusDesc,
    GdAdPrimaryStatus,
    GdAdResources,
    GdCampaignAction,
    GdCampaignPlatform,
    GdCampaignPrimaryStatus,
    GdCampaignPrimaryStatusDesc,
    GdCampaignStrategyType,
    GdCreativeType,
    GdGroupBlGenerationStatus,
    GdRetargetingConditionRuleType,
    GdShowConditionAutobudgetPriority,
    GdShowConditionPrimaryStatus,
    GdShowConditionType,
    GdSmartFilterPrimaryStatus,
    GdSmartFilterTab,
    GdSmartFilterTargetFunnel
} from './graphql-grid';
import { GridScreenAdditionalFilterItem, GridViewType } from './grid';
import { SmartGroupBudgetCalculation } from './groups';
import { FeedFilterCondition } from './groupsEditState';
import { ModerationRejectReason } from './moderation';
import { BannerViewType } from './pcode';
import { GridRecommendationsData } from './recommendations';
import { RetargetingConditionRuleType } from './retargeting';

export interface Stats {
    ctr?: number;
    profitability?: number;
    avgClickPosition?: string;
    cost?: number;
    conversionRate?: number;
    bounceRate?: number;
    revenue?: number;
    avgClickCost?: number;
    shows?: number;
    avgCpmCost?: number;
    avgShowPosition?: string;
    avgDepth?: string;
    clicks?: number;
    avgGoalCost?: number;
    goals?: number;
}

export type ScreenAdditionalFilter =
    | CampaignsAdditionalFilter
    | GroupsAdditionalFilter
    | BannersAdditionalFilter;

export enum AdditionalFiltersKeys {
    CAMPAIGNS = 'campaigns',
    GROUPS = 'groups',
    BANNERS = 'banners'
}

export type AdditionalFiltersDict = Record<
    AdditionalFiltersKeys,
    ScreenAdditionalFilter
>;

export enum CampaignsAdditionalFilter {
    ALL_ARCHIVED = 'ALL_CAMPAIGNS_ARCHIVED',
    ALL_ACTIVE = 'ALL_CAMPAIGNS_ACTIVE',
    ALL = 'ALL_CAMPAIGNS'
}

export enum CampaignsCalcTypeFilter {
    CPC = 'CPC',
    CPM = 'CPM'
}

export interface CalcTypeFilter {
    calcTypeFilterItems: Array<GridScreenAdditionalFilterItem>;
    calcTypeFilterValue?: CampaignsCalcTypeFilter;
}

export enum GroupsAdditionalFilter {
    ALL_ARCHIVED = 'ALL_GROUPS_ARCHIVED',
    ALL_ACTIVE = 'ALL_GROUPS_ACTIVE',
    ALL = 'ALL_GROUPS'
}

export enum BannersAdditionalFilter {
    ALL_ARCHIVED = 'ALL_BANNERS_ARCHIVED',
    ALL_ACTIVE = 'ALL_BANNERS_ACTIVE',
    ALL = 'ALL_BANNERS'
}

export interface StatusInfo {
    type: GdCampaignPrimaryStatus;
    hintType?: GdCampaignPrimaryStatusDesc;
    timeTargetingSettings?: TimeTargetingSettings;
}

export interface TimeTargetingSettings {
    startTime: moment.Moment;
    status: TimeTargetingStatus;
}

export enum TimeTargetingStatus {
    active = 'active',
    today = 'today',
    tomorrow = 'tomorrow',
    thisWeek = 'thisWeek',
    laterThanWeek = 'laterThanWeek'
}

export interface GridRow {
    id: string;
}

export interface Domain extends GridRow {
    stats: Stats;
}

export interface CampaignAccessInfo {
    // DIRECT-79228: в процессе общения с бэком выяснили,
    // что это устаревшее поле и лучше использовать EDIT_CAMP из actions
    canEdit: boolean;
    // действия доступные на кампанию
    actions: Array<GdCampaignAction>;
}

export interface CampaignBaseInfo extends GridRow {
    name: string;
    isAimingAllowed: boolean;
    brandSurveyId: string | null;
    brandSurveyStatus: BrandSurveyStatus | null;
    type: CampaignType;
    currency: CurrencyCode;
    chiefLogin: string;
    strategy: CampaignStrategy;
    statusInfo: StatusInfo;
    campaignUrl: Href;
    access: CampaignAccessInfo;
}

export interface ClientData {
    chiefLogin: string;
}

export interface Campaign extends Domain, CampaignBaseInfo {
    name: string;
    agencyId: string | null;
    type: CampaignType;
    budget: CampaignStrategyBudgetInfo;
    restSum: number;
    needPayment: boolean;
    moneyBlocked: boolean;
    stats: Stats;
    goalStats: Record<string, GoalStats>;
    hasBanners: boolean;
    recommendations: GridRecommendationsData;
    startDate: moment.Moment;
    endDate?: moment.Moment;
    id: string;
}

export enum CampaignPlatform {
    SEARCH = 'SEARCH',
    CONTEXT = 'CONTEXT',
    BOTH = 'BOTH'
}

export enum CampaignStatusForFilter {
    ARCHIVED = 'ARCHIVED',
    STOPPED = 'STOPPED',
    NO_MONEY = 'NO_MONEY',
    MODERATION_DENIED = 'MODERATION_DENIED',
    TEMPORARILY_PAUSED = 'TEMPORARILY_PAUSED',
    ACTIVE = 'ACTIVE',
    DRAFT = 'DRAFT',
    MODERATION = 'MODERATION'
}

// внимание! нельзя менять местами элементы этого перечисления,
// т.к. от них зависит порядок в фильтре DIRECT-81950
export enum CampaignType {
    TEXT = 'TEXT',
    CPM_BANNER = 'CPM_BANNER',
    MOBILE_CONTENT = 'MOBILE_CONTENT',
    PERFORMANCE = 'PERFORMANCE',
    MCBANNER = 'MCBANNER',
    DYNAMIC = 'DYNAMIC',
    CPM_DEALS = 'CPM_DEALS',
    CONTENT_PROMOTION = 'CONTENT_PROMOTION',
    CPM_YNDX_FRONTPAGE = 'CPM_YNDX_FRONTPAGE',
    INTERNAL_DISTRIB = 'INTERNAL_DISTRIB',
    INTERNAL_FREE = 'INTERNAL_FREE'
}

export enum GroupType {
    TEXT = 'TEXT',
    CPM_BANNER = 'CPM_BANNER',
    CPM_VIDEO = 'CPM_VIDEO',
    MOBILE_CONTENT = 'MOBILE_CONTENT',
    PERFORMANCE = 'PERFORMANCE',
    MCBANNER = 'MCBANNER',
    DYNAMIC = 'DYNAMIC',
    INTERNAL = 'INTERNAL',
    CPM_INDOOR = 'CPM_INDOOR',
    CPM_OUTDOOR = 'CPM_OUTDOOR',
    CPM_AUDIO = 'CPM_AUDIO',
    CPM_GEOPRODUCT = 'CPM_GEOPRODUCT',
    CPM_YNDX_FRONTPAGE = 'CPM_YNDX_FRONTPAGE'
}

export interface CampaignStrategyBaseFields {
    isAutoBudget: boolean;
    platform: GdCampaignPlatform;
}

export interface CampaignCommonStrategy extends CampaignStrategyBaseFields {
    type:
        | GdCampaignStrategyType.AVG_CLICK
        | GdCampaignStrategyType.AVG_CPA
        | GdCampaignStrategyType.AVG_CPA_PER_CAMP
        | GdCampaignStrategyType.AVG_CPA_PER_FILTER
        | GdCampaignStrategyType.AVG_CPC_PER_CAMP
        | GdCampaignStrategyType.AVG_CPC_PER_FILTER
        | GdCampaignStrategyType.AVG_CPI
        | GdCampaignStrategyType.CPM_DEFAULT
        | GdCampaignStrategyType.CPM_MAX_IMPRESSIONS
        | GdCampaignStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD
        | GdCampaignStrategyType.CPM_MAX_REACH
        | GdCampaignStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD
        | GdCampaignStrategyType.ROI
        | GdCampaignStrategyType.WEEK_BUDGET
        | GdCampaignStrategyType.WEEK_BUNDLE
        | GdCampaignStrategyType.NO_PREMIUM; // TODO: оторвать NO_PREMIUM когда оторвут в бэкэнде
}

export interface CampaignDefaultStrategy extends CampaignStrategyBaseFields {
    type: GdCampaignStrategyType.DEFAULT;
    separateBidding?: boolean;
}

export type CampaignStrategy = CampaignDefaultStrategy | CampaignCommonStrategy;

export enum AdvType {
    TEXT = 'TEXT',
    MOBILE_CONTENT = 'MOBILE_CONTENT',
    PERFORMANCE = 'PERFORMANCE',
    DYNAMIC = 'DYNAMIC',
    MCBANNER = 'MCBANNER',
    IMAGE_AD = 'IMAGE_AD',
    CPM_BANNER = 'CPM_BANNER',
    CPM_VIDEO = 'CPM_VIDEO',
    CPC_VIDEO = 'CPC_VIDEO',
    CPM_OUTDOOR = 'CPM_OUTDOOR',
    CPM_AUDIO = 'CPM_AUDIO',
    CPM_INDOOR = 'CPM_INDOOR',
    CPM_GEOPRODUCT = 'CPM_GEOPRODUCT',
    CPM_DEALS = 'CPM_DEALS',
    CPM_YNDX_FRONTPAGE = 'CPM_YNDX_FRONTPAGE',
    INTERNAL = 'INTERNAL'
}

export enum AdvTypeClient {
    TEXT_MOBILE = 'TEXT_MOBILE'
}

export enum PreviewType {
    NET = 'NET',
    SEARCH_DESC = 'SEARCH_DESC',
    SEARCH_MOBILE = 'SEARCH_MOBILE',
    SEARCH_BNO = 'SEARCH_BNO',
    NET_GROUP = 'NET_GROUP'
}

export interface PreviewSwitcherControlInfo {
    type: PreviewType;
    disabled: boolean;
}

export interface GroupItemActionAccess {
    canRemoderateAdsCallouts?: boolean;
    canAcceptAdsCalloutsModeration?: boolean;
    canBeDeleted?: boolean;
    canBeSentToBS?: boolean;
    canBeSentToModeration?: boolean;
    canBeSentToModerationByClient?: boolean;
    canBeSentToRemoderation?: boolean;
    canAcceptModeration?: boolean;
    canCopy?: boolean;
}

export interface GDGroupAccess extends GroupItemActionAccess {
    canEdit: boolean;
}

export enum GdGroupStatusNames {
    archived = 'archived',
    active = 'active',
    showing = 'showing',
    stopped = 'stopped'
}

export interface GdGroupStatus {
    bsEverSynced: boolean;
    blGenerationStatus?: GdGroupBlGenerationStatus;
    moderationStatus: GdAdGroupModerationStatus;
    primaryStatus: GdAdGroupPrimaryStatus;
    primaryStatusDesc?: GdAdGroupPrimaryStatusDesc;
}

export interface GroupBaseInfo extends GridRow {
    name: string;
    campaign: CampaignBaseInfo;
    access: GDGroupAccess;
    groupUrl: Href;
    status: GdGroupStatus;
    regionIds: number[];
    minusKeywords: string[];
    type?: GroupType;
}

export interface BannersGroupBaseInfoExtension {
    mainDomain?: string;
    storeHref?: string;
}

export interface FeedFiltersBaseInfoExtension {
    feedId: string;
}

export type BannersGroupBaseInfo = GroupBaseInfo &
    BannersGroupBaseInfoExtension;

export type FeedFiltersGroupBaseInfo = GroupBaseInfo &
    FeedFiltersBaseInfoExtension;

export interface Group extends Domain, GroupBaseInfo {
    access: GDGroupAccess;
    type: GroupType;
    recommendations: GridRecommendationsData;
    minusKeywords: string[];
    regionIds: number[];
    rejectReasons?: RejectReasonsData;
    goalStats: Record<string, GoalStats>;
    availableShowConditionTypes: GdShowConditionType[];
}

interface GDShowConditionAccess {
    canEdit: boolean;
}

export interface ShowCondition extends Domain {
    access: GDShowConditionAccess;
}

export interface Phrase extends ShowCondition {
    type: GdShowConditionType;
    isSuspended: boolean;
    groupId: string;
    group: GroupBaseInfo;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    showsForecast?: number;
    phrase: string;
    hasPhraseTrafaretAuction?: boolean;
    wCtr?: number;
    minusWords: string[];
    price?: number;
    trafficVolumeData?: Array<TrafficVolumeDataItem>;
    priceContext?: number;
    status: GdShowConditionPrimaryStatus;
    priceEditable: boolean;
    priceContextEditable: boolean;
    autobudgetPriorityEditable: boolean;
    isSearchVisibility: boolean;
    isContextVisibility: boolean;
    pokazometerData?: PokazometerData;
    bannersTotalCount: number;
    mainBanner?: Banner;
    goalStats: Record<string, GoalStats>;
}

export interface PhraseBidChange {
    id: string;
    groupId: string;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    exactPriceSearch?: number;
    exactPriceContext?: number;
}

export interface PhraseChange {
    id: string;
    groupId: string;
    phrase?: string;
    minusWords?: string[];
}

export interface PhraseEditPermissions {
    priceEditable: boolean;
    priceContextEditable: boolean;
    autobudgetPriorityEditable: boolean;
}

export interface CampaignPlatformVisibility {
    isSearchVisibility: boolean;
    isContextVisibility: boolean;
}

export interface Targeting extends ShowCondition {
    groupId: string;
    price?: number;
    group: GroupBaseInfo;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    status: GdShowConditionPrimaryStatus;
    isSuspended: boolean;
}

export interface Retargeting extends Domain {
    isEditable: boolean;
    group: GroupBaseInfo;
    name: string;
    description?: string;
    conditionRules: RetargetingConditionRule[];
    status: RetargetingStatus;
    priceContext: number;
    isPriceContextEnabled: boolean;
}

export interface RetargetingConditionRule {
    goals: RetargetingGoal[];
    type: GdRetargetingConditionRuleType;
}

export interface RetargetingConditionRuleGoal {
    id: string;
}

export interface RetargetingGoal extends RetargetingMapGoal {
    time?: number;
    isUnavailable: boolean;
    type: RetargetingConditionRuleType;
}

export interface RetargetingMapGoal {
    id: string;
    domain?: string;
    name: string;
    counterId?: number;
}

export enum RetargetingStatus {
    active = 'active',
    suspended = 'suspended'
}

export interface BannerStatus {
    type?: GdAdPrimaryStatus;
    isPreviousVersionShown: boolean;
    isRejectedOnModeration: boolean;
    isPlacementPagesRequired?: boolean;
    inactiveResources?: GdAdResources[];
}

export enum BannerImageType {
    TEXT = 'TEXT',
    BANNER = 'BANNER',
    CREATIVE = 'CREATIVE'
}

export interface BannerImageSize {
    height: number;
    width: number;
    isAdaptive?: boolean;
}

export interface BannerImageSmartCenter {
    x: number;
    y: number;
    w: number;
    h: number;
    ratio: string;
}

export interface BannerImageFormat {
    url: string;
    width: number;
    height: number;
    smartCenters: Array<BannerImageSmartCenter>;
}

export interface BannerImage extends BannerImageSize {
    name: string;
    url: string;
    duration?: string;
    isAdaptive?: boolean;
    type?: BannerImageType;
    formats?: Array<BannerImageFormat>;
}

export interface BannerImageForEdit extends BannerImage {
    creativeType?: GdCreativeType;
    isImage?: boolean;
    hash?: string;
    id?: string;
}

export interface BannerVideo {
    name: string;
    url: string;
}

export enum BannerStatusForFilter {
    ARCHIVED = 'ARCHIVED',
    MANUALLY_SUSPENDED = 'MANUALLY_SUSPENDED',
    ACTIVE = 'ACTIVE',
    MODERATION = 'MODERATION',
    MODERATION_REJECTED = 'MODERATION_REJECTED',
    DRAFT = 'DRAFT'
}

export interface GDBannerAccess {
    canEdit: boolean;
}

export interface RejectReasonsData {
    items: Array<ModerationRejectReason>;
    contactEmail?: string;
    error?: string;
}

export interface Banner extends Domain {
    isEditAllow: boolean;
    bannerType: AdvType;
    isAimingAllowed: boolean;
    exportId: string;
    title?: string;
    group: GroupBaseInfo;
    titleExtension?: string;
    body?: string;
    domain?: string;
    href?: string;
    status: BannerStatus;
    image?: BannerImage;
    video?: BannerVideo;
    creative?: BannerCreativeData;
    access: GDBannerAccess;
    recommendations: GridRecommendationsData;
    linkTail?: string;
    dynamicDisclaimer?: string;
    sitelinks: string[];
    age: string;
    warning: string;
    rejectReasons?: RejectReasonsData;
    bannerPrice?: BannerPrice;
    goalStats: Record<string, GoalStats>;
    description?: string;
    livePreviewUrl?: string;
    previewType?: BannerViewType;
    operators: OperatorModerationInfo[];
}

export interface FeedFilterStats {
    cost?: number;
    avgClickCost?: number;
    goals?: number;
}

export interface FeedFilter extends Domain {
    name: string;
    status: GdSmartFilterPrimaryStatus;
    id: string;
    targetFunnel: GdSmartFilterTargetFunnel;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    budgetCalculation: SmartGroupBudgetCalculation;
    group: FeedFiltersGroupBaseInfo;
    priceCpc?: number;
    priceCpa?: number;
    mainBanner?: Banner;
    bannersTotalCount: number;
    tab: GdSmartFilterTab;
    conditions: Array<FeedFilterCondition>;
    feedCategories: Array<FeedCategoryNode>;
    categoryIds: Array<string>;
    isAvailable: boolean;
    vendors: string;
    priceRange: NumberRangeInputValues;
    stats: FeedFilterStats;
}

export type BannerPreviewData = Pick<
    Banner,
    | 'id'
    | 'bannerType'
    | 'title'
    | 'group'
    | 'titleExtension'
    | 'body'
    | 'domain'
    | 'href'
    | 'status'
    | 'image'
    | 'video'
    | 'linkTail'
    | 'dynamicDisclaimer'
    | 'sitelinks'
    | 'age'
    | 'warning'
    | 'description'
    | 'livePreviewUrl'
    | 'previewType'
>;

export enum ContactsEditorType {
    LINK = 'LINK',
    TURBO = 'TURBO',
    VCARD = 'VCARD',
    ORGANIZATION = 'ORGANIZATION'
}

export enum ContactsForUserEditorType {
    MOBILE = 'MOBILE',
    DESKTOP = 'DESKTOP',
    MASS_ACTION = 'MASS_ACTION'
}

export enum BannerModerationTheme {
    distanceSales = 'distance_sales',
    pharmacy = 'pharmacy',
    medServices = 'med-services',
    banks = 'banks',
    law = 'law',
    medEquipment = 'med-equipment',
    optics = 'optics',
    notMedicine = 'not-medicine',
    acids = 'acids',
    psychology = 'psychology',
    veterinary = 'veterinary',
    popularMedicine = 'popular-medicine',
    alcohol = 'alcohol',
    tobacco = 'tobacco',
    dietarySuppl = 'dietarysuppl',
    pseudoWeapon = 'pseudoweapon',
    loan = 'loan',
    insurance = 'insurance',
    maternityCapital = 'maternity-capital',
    pawnshop = 'pawnshop',
    creditConsumerCooperative = 'credit-consumer-cooperative',
    mfi = 'mfi',
    payment = 'payment',
    forex = 'forex',
    forexBroker = 'forex',
    binaryOptions = 'binary-options',
    transport = 'transport',
    detective = 'detective',
    techInspection = 'tech-inspection',
    sportsNutrition = 'sports-nutrition',
    explosions = 'explosions',
    gamble = 'gamble',
    abortion = 'abortion',
    carNumbers = 'car-numbers',
    charity = 'charity',
    lottery = 'lottery',
    illegalDiet = 'illegal-diet',
    drugs = 'drugs',
    telemed = 'telemed',
    electronicOsago = 'electronic-osago',
    bookmaker = 'bookmaker',
    detectiveEquipment = 'detective-equipment',
    jewelry = 'jewelry',
    mediation = 'mediation',
    notary = 'notary',
    realtor = 'realtor',
    weapons = 'weapons',
    magic = 'magic',
    tattoo = 'tattoo',
    workAbroad = 'study',
    foreignTrade = 'foreign-trade',
    adOnTransport = 'transport-adv',
    cryptoCurrency = 'cryptocurrency',
    plus18 = 'plus18',
    age0 = 'age:0',
    age6 = 'age:6',
    age12 = 'age:12',
    age16 = 'age:16',
    age18 = 'age:18'
}

export const bannerModerationAgeFlags = {
    [BannerModerationTheme.plus18]: '18',
    [BannerModerationTheme.age18]: '18',
    [BannerModerationTheme.age16]: '16',
    [BannerModerationTheme.age12]: '12',
    [BannerModerationTheme.age6]: '6',
    [BannerModerationTheme.age0]: '0'
};

interface OperatorClientAccess {
    canEditInternationalization: boolean;
    canSearchPhrasesDuplicates: boolean;
    canSeeAgencyClients: boolean;
}

export interface OperatorChiefInfo {
    name: string;
    login: string;
    email: string;
    phone: string;
}

export interface OpereatorContact {
    description: string;
    cityName?: string;
    phone: string;
    phoneExtension?: string;
}

export interface OperatorOfficeInfo {
    cityContact: OpereatorContact;
    regionContact: OpereatorContact;
}

export interface Operator {
    tuneSecretKey: string;
    workCurrency: string;
    isSelfAgency: boolean;
    access: OperatorClientAccess;
    managerInfo?: OperatorChiefInfo;
    chiefInfo?: OperatorChiefInfo;
    agencyInfo?: OperatorChiefInfo;
    officeInfo: OperatorOfficeInfo;
}

export type TrafficVolumeDataItem = {
    trafficVolume: number;
    bid: number;
    amnestyPrice: number;
    showInTable?: boolean;
};

export type CoverageByPriceData = {
    coverage: number;
    price: number;
};

export type ClicksByPriceData = {
    clicks: number;
    price: number;
};

export type PokazometerData = {
    allCostsAndClicks: Array<ClicksByPriceData>;
    priceForCoverage: Array<CoverageByPriceData>;
    maxShows: number;
};

export type PriceConstants = {
    minPrice: number;
    maxPrice: number;
    minCpmPrice: number;
    maxCpmPrice: number;
    bigRate: number;
};

export type FilterGridViewDict = Partial<Record<GridViewType, string>>;

export enum CopyTooltipStatus {
    hidden = 'hidden',
    success = 'success',
    error = 'error'
}

export type CampaignId = string;
